// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { Tag } from "@quartzds/core";
import type { ComponentInterface, EventEmitter } from "@stencil/core";
import {
  Component,
  Element,
  Event,
  Fragment,
  h,
  Host,
  Prop,
  State,
} from "@stencil/core";
import type { LiteralUnion } from "type-fest";

import { onMobileBreakpoint } from "../utils";

export type Target = "_blank" | "_parent" | "_self" | "_top";

@Component({
  tag: "qds-web-megamenu-list-item",
  shadow: true,
  styleUrl: "megamenu-list-item.css",
})
export class MegamenuListItem implements ComponentInterface {
  @Element() public el!: HTMLElement;

  /**
   * Emitted when the megamenu is closed.
   */

  @Event({ eventName: "qds-nav-item:close" })
  public closeNavTabs: EventEmitter<boolean>;

  /**
   * Emitted when the megamenu is opened.
   */

  @Event({ eventName: "qds-nav-item:open" })
  public openNavTabs: EventEmitter<boolean>;

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<Target, string>;

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string;

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary?: string = "default";

  /**
   * Whether or not this megamenu list item will behave as `qds-web-title-link` or not.
   */
  @Prop() public readonly titleLink?: boolean;

  /**
   * The title link's tag.
   */
  @Prop() public tag?: Tag;

  /**
   * Primary text to display.
   */
  @Prop() public readonly text: string;

  /**
   * Text to display for the subtext.
   */
  @Prop() public readonly subtext?: string;

  /**
   * When set, clicking on this megamenu list item, will redirect to the href specified.
   *
   * @webnative
   */
  @Prop() public readonly href?: string;

  /**
   * When specified, the list item will show a photo on the left
   */
  @Prop() public readonly photo?: string;

  @State() public isMobile: boolean;

  private cleanupMobileListener?: () => void;

  get #hasPhoto(): boolean {
    return this.photo !== undefined && this.photo !== "";
  }

  get #hasIcon(): boolean {
    return this.iconName !== undefined && this.iconName !== "";
  }

  get #hasSubtext(): boolean {
    return this.subtext !== undefined && this.subtext !== "";
  }

  public componentWillLoad(): Promise<void> | void {
    this.cleanupMobileListener = onMobileBreakpoint((isMobile: boolean) => {
      this.isMobile = isMobile;
    }, this.el);
  }

  public disconnectedCallback(): void {
    this.cleanupMobileListener?.();
  }

  public render() {
    const Tag = this.href === undefined || this.href === "" ? "div" : "a";

    return (
      <Host role="presentation">
        <slot />
        {this.titleLink !== undefined && this.titleLink ? (
          <qds-web-title-link
            class={{
              "qds-mobile-link": this.isMobile,
              "qds-web-link": !this.isMobile,
            }}
            href={this.href}
            text={this.text}
            target={this.target}
            icon-name="navigation"
            icon-library="core"
            size="small"
            tag={this.tag}
          />
        ) : (
          <div
            role="listitem"
            class={{
              "qds-web-list-item": true,
              "qds-mobile": this.isMobile,
              "qds-web": !this.isMobile,
            }}
          >
            <Tag
              class="qds-item"
              role={Tag === "a" ? undefined : "presentation"}
              href={Tag === "a" ? this.href : undefined}
              target={Tag === "a" ? this.target : undefined}
              onClick={() => {
                this.itemClickHandler();
              }}
            >
              {!this.isMobile && (
                <>
                  {this.#hasPhoto && <img src={this.photo} alt="" />}
                  {this.#hasIcon && (
                    <qds-icon
                      aria-hidden="true"
                      name={this.iconName}
                      library={this.iconLibrary}
                    />
                  )}
                </>
              )}

              <div class="qds-texts">
                <div
                  class={{
                    "qds-title": true,
                    "qds-title-only": !this.#hasSubtext,
                  }}
                >
                  {this.text}
                </div>

                {this.#hasSubtext && (
                  <div class="qds-subtext">{this.subtext}</div>
                )}
              </div>

              {this.isMobile && (
                <qds-icon aria-hidden="true" library="core" name="navigation" />
              )}
            </Tag>
          </div>
        )}
      </Host>
    );
  }

  private itemClickHandler(): void {
    this.closeNavTabs.emit(true);
    if (!this.isMobile) {
      this.openNavTabs.emit(false);
    }
  }
}
